import { ApolloClient } from '@apollo/client'
import { bind } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import { exhaustMap, from, Observable, of, startWith } from 'rxjs'
import { catchError, map } from 'rxjs/operators'

import { logAndCaptureException } from '../../../../utils/errorTools'
import { left, LeftResult, right, RightResult } from '../../../../utils/result'
import { coerceCaughtToLeft } from '../../../../utils/rx/errors'
import { tapLeft } from '../../../../utils/rx/operators'
import { RemoveCartItemsError } from '../../errors'
import { enqueueCartWrite } from '../cart-write-queue'
import {
  buildRemoveCartItemsDocument,
  removeCartItemsVariables,
} from './cart-items-remove-document'

export interface RemoveCartItemsRequest {
  /** Correlates the emitted state with the call that asked for it; see {@link requestRemoveCartItems} */
  requestId: number
  items: ReadonlyArray<{ id: string }>
}

export type RemoveCartItemsResult = LeftResult<Error> | RightResult<void>

export interface RemoveCartItemsState {
  /** The request this state describes, or undefined before anything has been requested */
  requestId: number | undefined
  loading: boolean
  result: RemoveCartItemsResult | null
}

const [removeCartItemsRequest$, sendRemoveCartItemsRequest] = createSignal<RemoveCartItemsRequest>()

// module-scope counter rather than a timestamp: two clicks within the same millisecond must not
// share an id, or a caller could act on another request's result
let lastRequestId = 0

/**
 * Remove the given line items from the cart the write queue targets when the removal runs. Returns
 * the request id so a caller can tell its own result apart from an earlier one in
 * {@link useRemoveCartItems} — needed by consumers that act only on success (the
 * shipping-restriction gate keeps its modal open until its own removal succeeds).
 *
 * Callable from anywhere, not only a component: a click handler, an effect, or another observable.
 * Requests arriving while one is in flight are **dropped** (`exhaustMap`), so callers must disable
 * the control while `loading` — a dropped request never reports a result.
 */
export const requestRemoveCartItems = ({
  items,
}: Omit<RemoveCartItemsRequest, 'requestId'>): number => {
  lastRequestId += 1
  sendRemoveCartItemsRequest({ requestId: lastRequestId, items })
  return lastRequestId
}

const removeCartItemsFactory = (
  client: ApolloClient<unknown>,
  token: string | undefined,
  cartId: string,
  items: RemoveCartItemsRequest['items'],
) =>
  from(
    client.mutate({
      mutation: buildRemoveCartItemsDocument(items.length),
      variables: removeCartItemsVariables(cartId, items),
      ...(token && { context: { token } }),
      // override the global 'all' policy so Apollo throws on GraphQL errors and `catchError` can
      // convert them. Under 'all' a failed removal resolves successfully with `errors` populated,
      // which is what let callers treat a failure as success.
      errorPolicy: 'none',
    }),
  ).pipe(
    map(() => right(undefined)),
    catchError(coerceCaughtToLeft),
  )

/**
 * Remove several cart line items in ONE request, as a mutation carrying an aliased
 * `removeItemFromCart` field per item. Root mutation fields execute serially per the GraphQL spec,
 * so this keeps the "one removal at a time" invariant that parallel mutations against the same
 * Magento cart would break, while collapsing N round trips, N cart payloads, and N cache
 * normalizations into one of each.
 *
 * A mid-operation failure leaves the earlier removals applied (they are separate quote saves) and
 * reports `Left`, matching the previous sequential behaviour: the caller does not treat it as
 * success, and the returned cart reflects whatever was actually removed.
 */
const removeCartItems$: Observable<RemoveCartItemsState> = removeCartItemsRequest$.pipe(
  // ignore further requests until the in-flight one settles
  exhaustMap((request) =>
    request.items.length === 0
      ? of({ requestId: request.requestId, loading: false, result: right(undefined) })
      : enqueueCartWrite('items-remove', ({ client, cart, token }) =>
          removeCartItemsFactory(client, token, cart.id, request.items),
        ).pipe(
          // the target could not be resolved
          catchError(coerceCaughtToLeft),
          tapLeft(({ error }) => {
            logAndCaptureException(
              new RemoveCartItemsError(
                request.items.map((item) => item.id),
                error,
              ),
            )
          }),
          map((result) => ({
            requestId: request.requestId,
            loading: false,
            result: result._tag === 'Left' ? left(result.error) : right(undefined),
          })),
          startWith({
            requestId: request.requestId,
            loading: true,
            result: null,
          }),
        ),
  ),
)

/**
 * Loading and error state for the most recent removal. The default keeps the hook from suspending,
 * so it is safe in components with no `<Subscribe>` boundary (e.g. `ShippingDetails`).
 *
 * Error is captured before sharing the result - no need to capture again.
 */
export const [useRemoveCartItems, removeCartItemsState$] = bind(removeCartItems$, {
  requestId: undefined,
  loading: false,
  result: null,
})
